import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@saas-kit/ui';
import {
  DataTable,
  type BulkAction,
  type DataTableColumn,
  type PaginationState,
} from '@saas-kit/ui/DataTable';

import type { AdminUser } from './api';
import { UserActions } from './UserActions';
import { UserStatusBadge } from './UserStatusBadge';

type ActionKey = 'suspend' | 'reactivate';

interface Props {
  users: AdminUser[];
  pendingUserId: string | null;
  pendingAction: ActionKey | null;
  pagination: PaginationState;
  isLoading: boolean;
  error: string | null;
  bulkActions: BulkAction<AdminUser>[];
  onSuspend: (user: AdminUser) => void;
  onReactivate: (user: AdminUser) => void;
  search: string;
  onSearchChange: (next: string) => void;
  searchPlaceholder: string;
}

export function UsersTable({
  users,
  pendingUserId,
  pendingAction,
  pagination,
  isLoading,
  error,
  bulkActions,
  onSuspend,
  onReactivate,
  search,
  onSearchChange,
  searchPlaceholder,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('adminUsers');

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  const columns = useMemo<DataTableColumn<AdminUser>[]>(
    () => [
      {
        id: 'email',
        header: t('table.email'),
        cell: (row) => <span className="font-medium">{row.email}</span>,
      },
      {
        id: 'full_name',
        header: t('table.full_name'),
        cell: (row) => (
          <span className="text-muted-foreground">
            {row.full_name ?? t('table.full_name_unset')}
          </span>
        ),
        hideOnMobile: true,
        exportValue: (row) => row.full_name ?? '',
      },
      {
        id: 'status',
        header: t('table.status'),
        cell: (row) => <UserStatusBadge isActive={row.is_active} />,
        exportValue: (row) => (row.is_active ? 'active' : 'suspended'),
      },
      {
        id: 'flags',
        header: t('table.flags'),
        cell: (row) => (
          <div className="flex flex-wrap gap-1.5">
            {row.is_superuser ? (
              <Badge variant="warning">{t('flags.superuser')}</Badge>
            ) : null}
            {row.is_verified ? (
              <Badge variant="secondary">{t('flags.verified')}</Badge>
            ) : (
              <Badge variant="outline">{t('flags.unverified')}</Badge>
            )}
          </div>
        ),
        hideOnMobile: true,
        exportValue: (row) =>
          [row.is_superuser ? 'superuser' : null, row.is_verified ? 'verified' : 'unverified']
            .filter(Boolean)
            .join(','),
      },
      {
        id: 'created_at',
        header: t('table.created_at'),
        cell: (row) => (
          <span className="text-muted-foreground">
            {dateFormatter.format(new Date(row.created_at))}
          </span>
        ),
        hideOnMobile: true,
        exportValue: (row) => row.created_at,
      },
      {
        id: 'actions',
        header: t('table.actions'),
        align: 'end',
        exportSkip: true,
        cell: (row) => (
          <UserActions
            user={row}
            pendingAction={pendingUserId === row.id ? pendingAction : null}
            onSuspend={onSuspend}
            onReactivate={onReactivate}
          />
        ),
      },
    ],
    [t, dateFormatter, pendingUserId, pendingAction, onSuspend, onReactivate],
  );

  return (
    <DataTable<AdminUser>
      data={users}
      columns={columns}
      rowKey={(row) => row.id}
      testId="admin-users"
      selectable
      bulkActions={bulkActions}
      pagination={pagination}
      isLoading={isLoading}
      error={error}
      emptyText={t('empty.title')}
      exportable
      exportFilename="admin-users"
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
    />
  );
}
